import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import {
  faAt, faBox, faCalendar, faClinicMedical, faFile, faGraduationCap, faIdBadge, faSave, faSchool, faStethoscope, faSyringe, faUserGraduate, faCalendarStar,
  faFileUser, faMemo, faFileContract, faPenField
} from '@fortawesome/pro-solid-svg-icons'
import { Input, Modal, Select } from 'antd'
import { isArray, values, isUndefined } from 'lodash'
import moment from 'moment'

import FormItem from '../../../Components/shared/Forms/FormItem'
import { isValid, ValidationFields } from '../../../utils/validators'
import { firstOrDefault, getEntityOrNull, getIdOrNull, isObject, isOptimistic, validateString } from '../../../utils'
import { selectUser } from '../../../reducers/UserReducer'
import MultiPeriodsPicker from '../../../Components/antd/Pickers/MultiPeriodsPicker'
import {
  InternshipRequest, InternshipRequestProperties as RequestProperties, InternshipRequestValidators, mergeWithInstitution, mergeWithStudent, Origins
} from '../../../utils/entities/internshipRequest'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { InternshipFileTypes } from '../../../utils/entities/internshipRequestFile'
import useRequest from '../../../hooks/useRequest'
import { getAllStudents, SerializationContexts } from '../../../utils/api/student'
import { isEncoder, isHospitalAdmin, isInstitutionUser, isSchoolUser, isStudent } from '../../../utils/roles'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import StudentForm from '../StudentForm/StudentForm'
import { Permissions } from '../StudentForm/SearchStudentModalProvider'
import ExtendedStudentsSelector from '../../../Components/shared/Selectors/Student/ExtendedStudentsSelector'
import ExtendedSchoolsSelector from '../../../Components/shared/Selectors/School/ExtendedSchoolsSelector'
import SchoolOptionDisplayer from '../../../Components/shared/Selectors/School/SchoolOptionDisplayer'
import { fetchSchoolOptions, getSchoolSchoolSections } from '../../../utils/api/school'
import { StudentProperties } from '../../../utils/entities/student'
import { getInstitutionOptions } from '../../../utils/api/institution'
import useInstitutionSectors from '../../../utils/api/hooks/useInstitutionSectors'
import { reduceBySectionsAndYears } from '../../../utils/entities/schoolSection'
import ActTypesSelector from '../../../Components/shared/Selectors/ActTypesSelector'
import { getActTypes } from '../../../utils/api/schoolSection'
import ExtendedInstitutionsSelector from '../../../Components/shared/Selectors/Institution/ExtendedInstitutionsSelector'
import InstitutionOptionDisplayer from '../../../Components/shared/Selectors/Institution/InstitutionOptionDisplayer'
import EditableSectorSelector from '../../../Components/shared/Selectors/Sector/EditableSectorSelector'
import SectorFormModal from '../../../HOC/Forms/SectorFormModal'
import { TableActions } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { Properties } from '../../../utils/entities'
import { InstitutionOptions, SchoolOptions } from '../../../utils/constants'
import FilesUploader from '../../../Components/antd/Uploaders/FilesUploader'
import MetadataFields from '../../../Views/institution/InternshipsView/InternshipRequestsView/MetadataFields'
import { GlobalContext } from '../../GlobalProvider'
import SectionsSelector from '../../../Components/shared/Selectors/SectionsSelector'
import SectionToYearSelector from '../../../Components/shared/Selectors/SectionToYearSelector'
import QuestionBubble from '../../../Components/institution/Quotas/definition/QuestionBubble'
import { getAllInternshipRequestMetadata } from '../../../utils/api/internshipRequestMetadata'
import InternshipDurationItem from '../../../Components/shared/Forms/InternshipDurationItem'

const ADDED_SECTOR_NAME_KEY = 'added-sector-name'

const FIELD_WIDTH = '100%'
const FIELD_STYLE = { width: FIELD_WIDTH }

const { CONVENTION, COVER_LETTER, RESUME, OTHER } = InternshipFileTypes

const IndividualInternshipRequestForm = ({
  data, schools = { data: [] }, schoolSections = [], onSubmit
}) => {
  const { defaultSectorNames, sections } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [actTypes, setActTypes] = useState([])
  const [schoolSchoolSections, setSchoolSchoolSections] = useState([])
  const [schoolOptions, setSchoolOptions] = useState([])
  const [institutionOptions, setInstitutionOptions] = useState([])
  const [formData, setFormData] = useState({
    ...data,
    duration: data?.duration ?? data?.internshipDurationValue ?? null,
    internshipMasterEmail: data?.internshipMasterEmail ?? null,
    languageLocale: data?.languageLocale?.id !== -1 ? data.languageLocale : { languageTag: user.language ?? null },
    section: data?.sectionToYear?.section ?? null
  })
  const [alertModal, setAlertModal] = useState(null)
  const [sectorNameSearch, setSectorNameSearch] = useState('')
  const [files, setFiles] = useState({ [CONVENTION]: [], [COVER_LETTER]: [], [RESUME]: [], [OTHER]: [] })
  const [error, setError] = useState({ missingFields: [] })

  const localSchoolSections = useMemo(() => {
    let sections = []

    if (isArray(schoolSections) && schoolSections.length > 0) {
      sections = schoolSections
    } else if (isArray(schoolSchoolSections) && schoolSchoolSections.length > 0) {
      sections = schoolSchoolSections
    }

    return sections
  }, [schoolSections, schoolSchoolSections])
  const schoolHighlighted = useMemo(() => reduceBySectionsAndYears(localSchoolSections), [localSchoolSections])
  const localSection = useMemo(() => {
    let localSection = null

    if (isObject(formData.section)) {
      localSection = formData.section
    } else if (isObject(formData.sectionToYear)) {
      localSection = formData.sectionToYear.section
    }

    return localSection
  }, [formData.section, formData.sectionToYear])

  const [institutionSectors, setInstitutionSectors] = useInstitutionSectors(formData.institution)

  const isStudentUser = isStudent(user)
  const isHospitalUser = isHospitalAdmin(user)
  /** Typing an unregistered place would bypass the institutions a freemium school is limited to. */
  const canCreateInstitution = formData.school?.premium === true
  const isInstitutionRestricted = formData.requestOrigin !== Origins.MANUAL && !isStudentUser && isInstitutionUser(user)
  const isPortalOrigin = formData.requestOrigin === Origins.WEB_PORTAL

  const isActTypeMandatory = useMemo(() => {
    return schoolOptions.some(option => option.optionType.type === SchoolOptions.ACT_TYPE_MANDATORY_FOR_INTERNSHIP_REQUEST && option.optionEnabled)
  }, [schoolOptions])
  const isAgreementFileMandatory = useMemo(() => {
    return schoolOptions.some(option => option.optionType.type === SchoolOptions.AGREEMENT_FILE_MANDATORY_FOR_INTERNSHIP_REQUEST && option.optionEnabled)
  }, [schoolOptions])
  const isCoverLetterMandatory = useMemo(() => {
    return institutionOptions.some(option => option.optionType?.type === InstitutionOptions.COVER_LETTER && option.optionEnabled)
  }, [institutionOptions])
  const isResumeMandatory = useMemo(() => {
    return institutionOptions.some(option => option.optionType?.type === InstitutionOptions.RESUME && option.optionEnabled)
  }, [institutionOptions])
  /**
   * An unmanaged place has nobody reading its requests in Interneo : the internship master is the only way to reach it,
   * and the student is the only one knowing that address. A place typed in by hand is unmanaged by definition.
   */
  const isInternshipMasterEmailMandatory = isStudentUser && formData.institution?.managed !== true
  /** Neither the school nor the hospital fills it : they only ever see the address the request already carries. */
  const hasInternshipMasterEmail = (data?.internshipMasterEmail ?? '') !== ''
  /**
   * A place typed in by hand holds no care unit yet : the request names one, and the names offered are the very ones
   * that place will be seeded with once it is registered. Anything else the student knows better, he types himself.
   */
  const hasPreCreatedInstitution = (formData.institutionName ?? '') !== ''
  /** Nobody on the institution side maintains an unmanaged place's sectors : only the request itself can. */
  const isUnmanagedInstitution = isObject(formData.institution) && formData.institution.managed !== true
  /** The only path an encoder has into this form is the premium-gated school tab : no need to check it twice. */
  const canEditSectors = isEncoder(user)
  /**
   * Nobody keeps the care units of an unmanaged place up to date, so the ones it holds are a proposal rather than the
   * list of where a student may go : he names his own unit there too, exactly as he does for a place he types in.
   */
  const canNameSector = isStudentUser && isUnmanagedInstitution
  /** Neither the school nor the hospital names a care unit : they only ever read the one the request proposes. */
  const hasSectorName = (formData.sectorName ?? '') !== ''
  const isSectorNamed = hasPreCreatedInstitution || canNameSector || hasSectorName

  /**
   * The proposal is filtered against what is being searched rather than by the selector itself, so that the name typed
   * can be offered as a care unit of its own : the list is a proposal, not a closed set, and a place that is not
   * registered yet holds no care unit to pick from.
   */
  const sectorNameOptions = useMemo(() => {
    const typed = sectorNameSearch.trim()
    const lowered = typed.toLowerCase()
    /** A unit the place already holds comes first, and a default name it already carries is no second option. */
    const names = institutionSectors.map(sector => sector.name)
    const loweredNames = new Set(names.map(name => name.toLowerCase()))

    defaultSectorNames.forEach(name => {
      if (!loweredNames.has(name.toLowerCase())) {
        names.push(name)
        loweredNames.add(name.toLowerCase())
      }
    })

    const options = names
      .filter(name => name.toLowerCase().includes(lowered))
      .map(name => ({ key: name, value: name, label: name }))

    if (typed !== '' && !loweredNames.has(lowered)) {
      options.push({ key: ADDED_SECTOR_NAME_KEY, value: typed, label: t('internship_request_form.sector_name.add', { name: typed }) })
    }

    return options
  }, [defaultSectorNames, institutionSectors, sectorNameSearch, t])

  const parameters = useMemo(() =>
    isObject(formData.institution)
      ? { filters: { institution: formData.institution.id } }
      : {}
  , [formData.institution?.id])

  const metadataFields = useRequest({
    request: getAllInternshipRequestMetadata,
    parameters: parameters,
    enabled: isObject(parameters.filters)
  })

  useEffect(() => {
    if (schools.data.length === 1) {
      setFormData(data => ({ ...data, school: schools.data[0] }))
    }
  }, [schools.data])

  useEffect(() => {
    if (isObject(formData.sectionToYear) && localSchoolSections.length > 0) {
      const schoolSection = localSchoolSections.find(ss => ss.sectionToYear.id === formData.sectionToYear.id)

      if (isObject(schoolSection)) {
        getActTypes(user, schoolSection).then(json => {
          if (json?.data) {
            setActTypes(json.data)
          } else {
            setActTypes([])
          }
        })
      } else {
        setActTypes([])
      }
    }
  }, [isStudentUser, localSchoolSections, formData.sectionToYear])

  useEffect(() => {
    let active = true
    const institution = formData.institution
    const optionsFromForm = isObject(institution) ? institution.institutionOptions : null
    const institutionId = getIdOrNull(institution)

    if (isArray(optionsFromForm)) {
      setInstitutionOptions(prev => (prev === optionsFromForm ? prev : optionsFromForm))
    } else if (institutionId !== null && !isOptimistic(institution)) {
      setInstitutionOptions(prev => (prev.length === 0 ? prev : []))

      getInstitutionOptions(user, institution)
        .then(({ data }) => {
          if (active) {
            setInstitutionOptions(isArray(data) ? data : [])
          }
        })
        .catch(() => {
          if (active) {
            setInstitutionOptions(prev => (prev.length === 0 ? prev : []))
          }
        })
    } else {
      setInstitutionOptions(prev => (prev.length === 0 ? prev : []))
    }

    return () => {
      active = false
    }
  }, [formData.institution, user])

  useEffect(() => {
    if (isObject(formData.school) && schoolSections.length === 0) {
      getSchoolSchoolSections(user, formData.school, { version: 'v2' }).then(json => {
        const schoolSections = json?.data ? json.data.filter(ss => ss.sectionToYear) : []

        setSchoolSchoolSections(schoolSections)
      })

      if (isStudent(user)) {
        fetchSchoolOptions(user, formData.school).then(json => {
          if (json?.data) {
            setSchoolOptions(json.data)
          }
        })
      }
    }
  }, [schoolSections.length, user, formData.school])

  /** A request may carry its student in plain fields alone : the panel then still has someone to show. */
  const formStudent = useMemo(() => (getIdOrNull(formData.student) || formData.email) ? new InternshipRequest(formData).getStudent() : null, [formData])

  const students = useRequest({
    request: getAllStudents,
    parameters: useMemo(() => ({
      paginate: false,
      filters: {
        schools: [getIdOrNull(formData.school)],
        institutions: isInstitutionUser(user) ? [getIdOrNull(user.institutions[0])] : []
      },
      contexts: [SerializationContexts.SECTION, SerializationContexts.NISS]
    }), [user, formData.school]),
    enabled: !!getIdOrNull(formData.school)
  })

  const errorClass = useMemo(() => error.missingFields.reduce((acc, f) => {
    if (f === Properties.FIRST_NAME) {
      f = StudentProperties.FIRSTNAME
    } else if (f === Properties.LAST_NAME) {
      f = StudentProperties.LASTNAME
    }

    acc[f] = 'error-ant-border'

    return acc
  }, {}), [error])

  const handleSubmit = useCallback(() => {
    const internshipRequest = new InternshipRequest(formData)
    const { section } = formData
    const formValidators = { ...InternshipRequestValidators, section: r => getIdOrNull(r.section) !== null }

    const errors = isValid({ ...internshipRequest, section }, values(formValidators))

    if (isActTypeMandatory && internshipRequest.actType === null) {
      errors.keys.push(RequestProperties.ACT_TYPE)
    }

    if (isAgreementFileMandatory && files[CONVENTION].length === 0 && isOptimistic(internshipRequest)) {
      errors.keys.push(RequestProperties.FILES)
    }

    if (isResumeMandatory && files[RESUME].length === 0 && isOptimistic(internshipRequest)) {
      errors.keys.push(ValidationFields.RESUME)
    }

    if (isInternshipMasterEmailMandatory && !validateString(internshipRequest.internshipMasterEmail, { notEmpty: true, isEmail: true }).valid) {
      errors.keys.push(RequestProperties.INTERNSHIP_MASTER_EMAIL)
    }

    if (isCoverLetterMandatory && files[COVER_LETTER].length === 0 && isOptimistic(internshipRequest)) {
      errors.keys.push(ValidationFields.COVER_LETTER)
    }

    if (metadataFields.data.length > 0) {
      metadataFields.data.forEach(field => {
        if (field.isMandatory && (internshipRequest.metadata === null || !internshipRequest.metadata[field.name])) {
          errors.keys.push('metadata_' + field.name)
        }
      })
    }

    if (errors.keys.length === 0) {
      internshipRequest.createdAt = moment()
      const filesAsArray = []

      Object.keys(files).forEach(key => {
        files[key].forEach(f => filesAsArray.push({ file: f, type: key }))
      })

      onSubmit(new InternshipRequest({ ...internshipRequest, student: formStudent }), filesAsArray)
    } else {
      const details = {}

      Object.keys(errors.messages).forEach(key => {
        details[t(key)] = errors.messages[key].map(m => t(m))
      })

      setError({
        ...error,
        title: t('form.errors'),
        details,
        visible: true,
        missingFields: errors.keys
      })
    }
  }, [formData, formStudent, files, error, isActTypeMandatory, isAgreementFileMandatory, isCoverLetterMandatory, isInternshipMasterEmailMandatory, isResumeMandatory, t, metadataFields, onSubmit])

  const handleUpload = useCallback((file, key) => {
    setFiles({ ...files, [key]: [...files[key], file] })
  }, [files, setFiles])

  const handleUploadCv = useCallback(file => handleUpload(file, RESUME), [handleUpload])
  const handleUploadMotivationLetter = useCallback(file => handleUpload(file, COVER_LETTER), [handleUpload])
  const handleUploadConvention = useCallback(file => handleUpload(file, CONVENTION), [handleUpload])
  const handleUploadOther = useCallback(file => handleUpload(file, OTHER), [handleUpload])

  const handleRemove = useCallback((file, key) => {
    setFiles({ ...files, [key]: files[key].filter(f => f.uid !== file.uid) })
  }, [files, setFiles])

  const handleRemoveCv = useCallback(file => handleRemove(file, RESUME), [handleRemove])
  const handleRemoveMotivationLetter = useCallback(file => handleRemove(file, COVER_LETTER), [handleRemove])
  const handleRemoveConvention = useCallback(file => handleRemove(file, CONVENTION), [handleRemove])
  const handleRemoveOther = useCallback(file => handleRemove(file, OTHER), [handleRemove])
  const filterOption = useCallback((input, option) => (option.props.label ?? '').toLowerCase().includes(input.toLowerCase()), [])

  const handleSelect = useCallback(key => (value, _, entity) => setFormData(data => ({ ...data, [key]: getEntityOrNull(entity) ?? getEntityOrNull(value) })), [])
  const handleValueChange = useCallback(key => value => setFormData(data => ({ ...data, [key]: value })), [])
  /** Clearing the field hands an undefined over, which an update would read as "leave the name as it is". */
  const handleSectorNameChange = useCallback(value => {
    const name = (value ?? '') === '' ? null : value
    /** A unit the place already holds is picked rather than named : only a name it does not carry travels as text. */
    const sector = name === null ? null : institutionSectors.find(sector => sector.name === name) ?? null

    setSectorNameSearch('')
    setFormData(data => ({
      ...data,
      [RequestProperties.SECTOR]: sector,
      [RequestProperties.SECTOR_NAME]: sector === null ? name : null
    }))
  }, [institutionSectors])
  /** An emptied field names no master : the backend only ever accepts an address it can parse, or nothing at all. */
  const handleInternshipMasterEmailChange = useCallback(e => {
    const email = e.target.value.trim()

    setFormData(data => ({ ...data, [RequestProperties.INTERNSHIP_MASTER_EMAIL]: email === '' ? null : email }))
  }, [])
  const handleSectionChange = useCallback(value => setFormData(data => ({ ...data, section: getEntityOrNull(value), sectionToYear: null })), [])
  const handleSectorChange = useCallback(value => setFormData(data => ({
    ...data,
    [RequestProperties.SECTOR]: getEntityOrNull(value),
    [RequestProperties.SECTOR_NAME]: null
  })), [])
  const handleEditStudent = useCallback(student => setFormData(data => ({ ...data, ...mergeWithStudent(data, student) })), [])
  /** The selector hands the id over as its value : without the option entity, the school loses its premium flag. */
  const handleSchoolChange = useCallback((value, _, entity) => {
    const school = getEntityOrNull(entity) ?? getEntityOrNull(value)

    if (isStudentUser) {
      /** Each school reaches its own institutions : the one picked for the previous school no longer stands. */
      setFormData(data => ({ ...mergeWithInstitution(data, null), school, section: null, sectionToYear: null }))
    } else {
      setFormData(data => ({ ...data, school, section: null, sectionToYear: null, student: null }))
    }
  }, [isStudentUser])
  /** The selector hands the id over as its value : without the option entity, the institution loses its managed flag. */
  const handleInstitutionChange = useCallback((value, _, entity) => {
    setFormData(data => mergeWithInstitution(data, getEntityOrNull(entity) ?? getEntityOrNull(value)))
  }, [])

  const handleStudentChange = useCallback((id, _, student) => {
    const newFormData = { ...formData, student: getEntityOrNull(student) }

    if (isObject(student)) {
      const studentSection = student.sections.find(ss => ss.school.id === formData.school.id)
      const compatibleSections = sections.filter(s => isObject(studentSection) && s.id === studentSection.sectionToYear.section.id)
      const resetStudent = () => {
        setFormData(formData => ({ ...formData, student: null }))
        setAlertModal(null)
      }

      if (compatibleSections.length === 0) {
        setAlertModal({
          title: 'internship_request_form.alert_modal.invalid_student.title',
          body: 'internship_request_form.alert_modal.invalid_student.body',
          okButtonText: t('internship_request_form.alert_modal.invalid_student.ok_button'),
          cancelButtonText: t('common.cancel'),
          handleOK: resetStudent,
          handleCancel: resetStudent
        })
      } else if (localSection === null) {
        newFormData.section = studentSection.sectionToYear.section
        newFormData.sectionToYear = null
      } else if (localSection.id !== studentSection.sectionToYear.section.id) {
        setAlertModal({
          title: 'internship_request_form.incompatible_section_and_student.title',
          body: 'internship_request_form.incompatible_section_and_student.body',
          okButtonText: t('internship_request_form.incompatible_section_and_student.ok_button'),
          cancelButtonText: t('internship_request_form.alert_modal.invalid_student.cancel_button'),
          handleOK: () => {
            setFormData(formData => ({ ...formData, section: studentSection.sectionToYear.section, sectionToYear: null }))
            setAlertModal(null)
          },
          handleCancel: resetStudent
        })
      }
    }

    setFormData(newFormData)
  }, [formData, sections])

  const handleStudentSubmit = useCallback(student => {
    const schoolSection = firstOrDefault(student.sections)

    setFormData(form => {
      const seed = isOptimistic(student) ? mergeWithStudent(form, student) : form
      let section = form.section
      let sectionToYear = null

      if (!isOptimistic(student)) {
        section = schoolSection?.sectionToYear ? schoolSection.sectionToYear.section : null
      } else {
        sectionToYear = form.sectionToYear
      }

      return {
        ...seed,
        student: isOptimistic(student) ? null : getEntityOrNull(student),
        school: getEntityOrNull(schoolSection === null || isUndefined(schoolSection.school) ? form.school : schoolSection.school),
        section,
        sectionToYear
      }
    })
  }, [])

  const handleInstitutionSubmit = useCallback(institution => {
    if (institution) {
      setFormData(data => mergeWithInstitution(data, institution))
    }
  }, [])

  const handleSectorSaved = useCallback((savedSector, action) => {
    setInstitutionSectors(sectors => (
      action === TableActions.CREATE
        ? [...sectors, savedSector]
        : sectors.map(s => (s.id === savedSector.id ? savedSector : s))
    ))

    if (action === TableActions.CREATE) {
      setFormData(data => ({ ...data, [RequestProperties.SECTOR]: savedSector }))
    }
  }, [])

  const isExternalStudent = useMemo(() => {
    return isObject(formData.student) && getIdOrNull(formData.student) > 0 && !students.data.some(s => getIdOrNull(s) === getIdOrNull(formData.student))
  }, [students.data, formData.student])
  const studentList = useMemo(() => isExternalStudent ? [...students.data, formData.student] : students.data, [students.data, isExternalStudent, formData.student])
  const isDualColumns = !isStudentUser || isOptimistic(formData)
  const leftClassName = isDualColumns ? 'responsive-w-50' : 'w-100'

  const handleMetadataChange = (fieldName, value) => {
    setFormData(formData => ({
      ...formData,
      [Properties.METADATA]: {
        ...formData[Properties.METADATA],
        [fieldName]: value
      }
    }))
  }

  return (
    <>
      <div className='responsive-flex-row'>
        <div className={leftClassName}>
          <h3> {t('internship_request_from.titles.internship')} </h3>
          <FormItem icon={faBox} label='form.label.request_origin'>
            <Input
              value={t(
                'request_origin.' +
                  (formData.requestOrigin ?? '').toLowerCase()
              )}
              disabled
            />
          </FormItem>
          <FormItem icon={faSchool} label='form.label.school' required>
            <ExtendedSchoolsSelector
              renderOption={entity => <SchoolOptionDisplayer entity={entity} />}
              className={errorClass[RequestProperties.SCHOOL]}
              disabled={isInstitutionRestricted}
              dropdownDisabled={isStudentUser}
              reverse
              style={FIELD_STYLE}
              values={getIdOrNull(formData.school)}
              onChange={handleSchoolChange}
            />
          </FormItem>
          <div className='flex-row w-100'>
            <FormItem
              className='w-50 pdr-4'
              icon={faGraduationCap}
              label='form.label.section'
              help={
                <QuestionBubble
                  title={t('internship_request_form.help.section')}
                  style={{ marginLeft: '8px' }}
                />
              }
              required
            >
              <SectionsSelector
                className={errorClass[ValidationFields.SECTION]}
                style={FIELD_STYLE}
                values={getIdOrNull(localSection)}
                onChange={handleSectionChange}
                disabled={
                  sections.length === 0 ||
                  (getIdOrNull(formData.student) !== null && !isStudentUser)
                }
                showSearch
                highlightValues={schoolHighlighted.sections}
                multiple='default'
              />
            </FormItem>
            <FormItem
              className='w-50 pdl-4'
              icon={faCalendarStar}
              label='form.label.year'
              help={
                <QuestionBubble
                  title={t('internship_request_form.help.section_to_year')}
                  style={{ marginLeft: '8px' }}
                />
              }
              required
            >
              <SectionToYearSelector
                className={errorClass[RequestProperties.SECTION_TO_YEAR]}
                style={FIELD_STYLE}
                values={getIdOrNull(formData.sectionToYear)}
                section={localSection}
                onChange={handleSelect(RequestProperties.SECTION_TO_YEAR)}
                disabled={!getIdOrNull(localSection) || isInstitutionRestricted}
                highlightValues={
                  schoolHighlighted.sectionToYearsBySection[
                    getIdOrNull(localSection)
                  ] ?? []
                }
                multiple='default'
              />
            </FormItem>
          </div>
          {!isHospitalUser && formData.sectionToYear && (
            <FormItem
              className={errorClass[RequestProperties.ACT_TYPE]}
              icon={faSyringe}
              label='form.label.act_type'
              required={isActTypeMandatory}
            >
              <ActTypesSelector
                actTypes={actTypes}
                disabled={isInstitutionUser(user)}
                empty
                style={FIELD_STYLE}
                values={
                  actTypes.length > 0
                    ? getIdOrNull(formData.actType)
                    : formData.actType?.name
                }
                onChange={handleSelect(RequestProperties.ACT_TYPE)}
              />
            </FormItem>
          )}
          <FormItem icon={faClinicMedical} label='form.label.place' required>
            <ExtendedInstitutionsSelector
              renderOption={entity => <InstitutionOptionDisplayer entity={entity} />}
              canCreateInstitution={canCreateInstitution}
              className={
                'institution-card-selector ' +
                (errorClass[RequestProperties.INSTITUTION] ?? '')
              }
              disabled={
                isHospitalUser ||
                isPortalOrigin ||
                (isStudentUser && getIdOrNull(formData.school) === null)
              }
              dropdownDisabled={!isStudentUser}
              school={formData.school}
              style={{ ...FIELD_STYLE }}
              values={
                formData.institutionName ?? getIdOrNull(formData.institution)
              }
              onChange={handleInstitutionChange}
              onInstitutionSubmit={handleInstitutionSubmit}
            />
          </FormItem>
          <FormItem icon={faStethoscope} label='form.label.sector'>
            {isSectorNamed ? (
              /** `optionLabelProp` : the added option reads "add <name>", and that sentence is no care unit. */
              <Select
                className={errorClass[RequestProperties.SECTOR]}
                disabled={!isStudentUser}
                filterOption={false}
                optionLabelProp='label'
                placeholder={t('internship_request_form.sector_name.placeholder')}
                style={FIELD_STYLE}
                value={formData.sectorName ?? formData.sector?.name ?? undefined}
                onChange={handleSectorNameChange}
                onSearch={setSectorNameSearch}
                showSearch
                allowClear
              >
                {sectorNameOptions.map(option => (
                  <Select.Option key={option.key} value={option.value} label={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            ) : isUnmanagedInstitution && canEditSectors ? (
              <SectorFormModal institutions={[formData.institution]} onSave={handleSectorSaved}>
                <EditableSectorSelector
                  className={errorClass[RequestProperties.SECTOR]}
                  disabled={getIdOrNull(formData.institution) === null}
                  institution={formData.institution}
                  sectors={institutionSectors}
                  style={FIELD_STYLE}
                  value={getIdOrNull(formData.sector)}
                  onChange={handleSelect(RequestProperties.SECTOR)}
                />
              </SectorFormModal>
            ) : (
              <Select
                className={errorClass[RequestProperties.SECTOR]}
                disabled={
                  getIdOrNull(formData.institution) === null ||
                  institutionSectors.length === 0
                }
                style={FIELD_STYLE}
                value={getIdOrNull(formData.sector)}
                onChange={handleSectorChange}
                filterOption={filterOption}
                showSearch
              >
                <Select.Option key={null} value={null} label='/'>
                  {' '}
                  /{' '}
                </Select.Option>
                {institutionSectors.map((s) => (
                  <Select.Option key={s.id} value={s.id} label={s.name}>
                    {' '}
                    {s.name}{' '}
                  </Select.Option>
                ))}
              </Select>
            )}
          </FormItem>
          <FormItem icon={faCalendar} label='form.label.periods' required>
            <MultiPeriodsPicker
              className={errorClass[RequestProperties.REQUEST_PERIODS]}
              periods={formData.requestPeriods}
              onChange={handleValueChange(RequestProperties.REQUEST_PERIODS)}
            />
          </FormItem>
          <InternshipDurationItem
            disabled={isInstitutionRestricted}
            duration={formData.duration}
            onDurationChange={handleValueChange(RequestProperties.DURATION)}
          />
          <FormItem icon={faPenField} label='form.label.comment'>
            <Input.TextArea
              maxLength={500}
              rows={4}
              value={formData.comment}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  [RequestProperties.COMMENT]: e.target.value
                })}
            />
          </FormItem>
          {(isStudentUser || hasInternshipMasterEmail) && (
            <FormItem icon={faAt} label='form.label.supervisor_email' required={isInternshipMasterEmailMandatory}>
              <Input
                className={errorClass[RequestProperties.INTERNSHIP_MASTER_EMAIL]}
                disabled={!isStudentUser}
                value={formData.internshipMasterEmail ?? ''}
                onChange={handleInternshipMasterEmailChange}
              />
            </FormItem>
          )}
          {formData.id > 0 && (
            <FormItem icon={faIdBadge} label='form.label.id'>
              {' '}
              <Input value={formData.id} disabled />{' '}
            </FormItem>
          )}
        </div>
        {isDualColumns && (
          <div className='responsive-w-50 responsive-ml-20'>
            {!isStudentUser && (
              <>
                <h3> {t('internship_request_from.titles.student')} </h3>
                <FormItem
                  icon={faUserGraduate}
                  label='internship_request_from.titles.student'
                  required
                >
                  <ExtendedStudentsSelector
                    style={FIELD_STYLE}
                    disabled={
                      getIdOrNull(formData.school) === null ||
                      students.loading ||
                      isSchoolUser(user) ||
                      formData.requestOrigin !== Origins.MANUAL
                    }
                    highlighted={schoolHighlighted}
                    school={formData.school}
                    students={studentList}
                    values={
                      isOptimistic(getIdOrNull(formData.student))
                        ? null
                        : getIdOrNull(formData.student)
                    }
                    loading={students.loading}
                    showSearch
                    onChange={handleStudentChange}
                    onStudentSubmit={handleStudentSubmit}
                    notFoundContent={t(
                      'internship_request_from.student_selector_dropdown.no_data'
                    )}
                  />
                </FormItem>
                {getIdOrNull(formStudent) && (
                  <StudentForm
                    data={formStudent}
                    errorFields={error.missingFields}
                    permission={
                      isOptimistic(formStudent)
                        ? Permissions.WRITE
                        : Permissions.READ_ONLY
                    }
                    onChange={handleEditStudent}
                  />
                )}
              </>
            )}
            <MetadataFields
              metadataFields={metadataFields.data}
              values={formData.metadata}
              errorFields={errorClass}
              onChange={handleMetadataChange}
            />
            {isOptimistic(formData) && (
              <>
                <FormItem
                  icon={faFileUser}
                  label='form.label.cv'
                  required={isResumeMandatory}
                >
                  <FilesUploader
                    className={errorClass[ValidationFields.RESUME]}
                    files={files[RESUME]}
                    onUpload={handleUploadCv}
                    onRemove={handleRemoveCv}
                  />
                </FormItem>
                <FormItem
                  icon={faMemo}
                  label='form.label.motivation_letter'
                  required={isCoverLetterMandatory}
                >
                  <FilesUploader
                    className={errorClass[ValidationFields.COVER_LETTER]}
                    files={files[COVER_LETTER]}
                    onUpload={handleUploadMotivationLetter}
                    onRemove={handleRemoveMotivationLetter}
                  />
                </FormItem>
                <FormItem
                  icon={faFileContract}
                  label='form.label.convention'
                  required={isAgreementFileMandatory}
                >
                  <FilesUploader
                    className={errorClass[RequestProperties.FILES]}
                    files={files[CONVENTION]}
                    onUpload={handleUploadConvention}
                    onRemove={handleRemoveConvention}
                  />
                </FormItem>
                <FormItem icon={faFile} label='form.label.other_files'>
                  <FilesUploader
                    files={files[OTHER]}
                    onUpload={handleUploadOther}
                    onRemove={handleRemoveOther}
                  />
                </FormItem>
              </>
            )}
          </div>
        )}
      </div>
      <div className='flex-row w-100 j-ctn-e'>
        <IconButton
          type='primary'
          icon={faSave}
          onClick={handleSubmit}
          text={t('actions.save')}
        />
      </div>
      {alertModal !== null && (
        <Modal
          title={t(alertModal.title)}
          visible
          okText={t(alertModal.okButtonText)}
          cancelText={t(alertModal.cancelButtonText)}
          onCancel={alertModal.handleCancel}
          onOk={alertModal.handleOK}
        >
          {t(alertModal.body)}
        </Modal>
      )}
    </>
  )
}

export default IndividualInternshipRequestForm
